import { GtmhubController } from "@gtmhub/core";
import { IComponentOptions, IOnChangesObjectOf } from "angular";
import { IPlanningSession } from "@gtmhub/sessions";
import { byEndDescAndStartAsc } from "@gtmhub/customFields/services/query-sessions.service";

export interface ISessionListSelectorItem {
  id: string;
  name: string;
  color: string;
  selected: boolean;
}

/**
 * Provides functionality for selecting sessions from a list of filtered by title sessions
 *
 * `selected-ids` array of all selected session ids for a custom field.
 * `filtered-sessions` filtered sessions by title which will construct the list.
 *
 * @example
 * <session-list-selector
 *   custom-field="$ctrl.customField"
 *   session-tags="$ctrl.sessionTags"
 * </session-list-selector>
 */

export interface ISessionListSelectorComponentBindings {
  selectedIds: string[];
  filteredSessions: IPlanningSession[];
  onSelection?(args: { sessionId: string }): void;
  onDeselection?(args: { sessionId: string }): void;
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface SessionListSelectorComponentCtrl extends ISessionListSelectorComponentBindings {}

export class SessionListSelectorComponentCtrl extends GtmhubController {
  list: ISessionListSelectorItem[];

  $onInit(): void {
    this.list = this.createList();
  }

  $onChanges(onChangesObj: IOnChangesObjectOf<ISessionListSelectorComponentBindings>): void {
    if (onChangesObj.filteredSessions && !onChangesObj.filteredSessions.isFirstChange()) {
      this.list = this.createList();
    }

    if (onChangesObj.selectedIds && !onChangesObj.selectedIds.isFirstChange()) {
      this.onSelectedIdsChange(onChangesObj.selectedIds.previousValue, onChangesObj.selectedIds.currentValue);
    }
  }

  onSelect(item: ISessionListSelectorItem): void {
    if (this.onSelection) {
      this.onSelection({ sessionId: item.id });
    }
  }

  onDeselect(item: ISessionListSelectorItem): void {
    if (this.onDeselection) {
      this.onDeselection({ sessionId: item.id });
    }
  }

  private onSelectedIdsChange(previousSelectedId: string[], currentSelectedIds: string[]): void {
    if (!currentSelectedIds) {
      currentSelectedIds = [];
    }

    if (!previousSelectedId) {
      previousSelectedId = [];
    }

    const deselectedIds = previousSelectedId.filter((id) => !currentSelectedIds.includes(id));
    deselectedIds.forEach((id) => {
      this.markNodeAsDeselected(id);
    });

    const selectedIds = currentSelectedIds.filter((id) => !previousSelectedId.includes(id));
    selectedIds.forEach((id) => {
      this.markNodeAsSelected(id);
    });
  }

  private markNodeAsSelected(id: string) {
    const node = this.list.find((node) => node.id === id);
    if (node && !node.selected) {
      node.selected = true;
    }
  }

  private markNodeAsDeselected(id: string) {
    const node = this.list.find((node) => node.id === id);
    if (node && node.selected) {
      node.selected = false;
    }
  }

  private createList(): ISessionListSelectorItem[] {
    const sessionsCopy = this.filteredSessions.sort(byEndDescAndStartAsc);
    const filteredSessionItems: ISessionListSelectorItem[] = [];

    for (let i = 0; i < sessionsCopy.length; i++) {
      const session = sessionsCopy[i];
      filteredSessionItems.push(this.createSessionListItem(session.id));
    }

    return filteredSessionItems;
  }

  private createSessionListItem(sessionId: string): ISessionListSelectorItem {
    const session = this.filteredSessions.find((session) => session.id === sessionId);
    return {
      id: sessionId,
      name: session.title,
      color: session.color,
      selected: !!(this.selectedIds && this.selectedIds.includes(sessionId)),
    } as ISessionListSelectorItem;
  }

  onSelectionToggle(item: ISessionListSelectorItem): void {
    if (item.selected && this.onSelection) {
      this.onSelect(item);
    } else if (!item.selected && this.onDeselection) {
      this.onDeselect(item);
    }
  }
}

export const SessionListSelectorComponent: IComponentOptions = {
  templateUrl: require("./session-list-selector.jade"),
  controller: SessionListSelectorComponentCtrl,
  bindings: {
    selectedIds: "<",
    filteredSessions: "<",
    onSelection: "&",
    onDeselection: "&",
  },
};
